import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const faqItems = [
  {
    question: '麻花影院提供哪些类型的电影资源？',
    answer: '麻花影院聚合了最新电影高清大片资源，涵盖动作片、科幻片、喜剧片、爱情片、恐怖片、动画片、战争片、纪录片等八大品类。我们每日更新，确保您能第一时间观看到最新上线的热门影片。所有资源均经过精心筛选，画质高清，播放流畅。',
  },
  {
    question: '麻花影院是否需要付费观看？',
    answer: '麻花影院始终坚持免费服务，所有电影资源均可免费在线观看，无需注册会员或支付任何费用。我们致力于为影迷朋友打造最便捷、最实惠的观影体验，让每个人都能轻松享受电影的魅力。',
  },
  {
    question: '如何搜索我想看的电影？',
    answer: '您可以通过首页的分类导航快速找到感兴趣的电影类型，也可以在分类页面浏览所有电影。我们按照动作片、科幻片、喜剧片等八大类型对影片进行了详细分类，便于您精准查找。',
  },
  {
    question: '麻花影院的电影更新频率是怎样的？',
    answer: '我们的内容团队每日更新影片资源，确保用户能够第一时间观看到最新上映的热门电影。无论是刚下映的院线大片，还是经典的影史佳作，都在我们的更新计划之中。',
  },
  {
    question: '麻花影院的画质和播放体验如何？',
    answer: '麻花影院所有影片均提供高清画质，支持流畅播放。我们采用先进的视频压缩和传输技术，确保在各类网络环境下都能获得出色的观影体验。同时，我们不断优化播放器性能，带来丝滑流畅的观影感受。',
  },
];

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于麻花影院',
        url: 'https://mahua-cinema.readdy.ai/about',
        isPartOf: { '@type': 'WebSite', name: '麻花影院' },
      },
      {
        '@type': 'Organization',
        name: '麻花影院',
        url: 'https://mahua-cinema.readdy.ai',
        description: '麻花影院聚合最新电影高清大片资源，包含热门动作片、科幻片、喜剧电影等，免费在线观看更新不断。',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mahua-cinema.readdy.ai' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://mahua-cinema.readdy.ai/about' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <main>
            {/* Page Header */}
            <section className="px-4 md:px-6 py-10 md:py-16">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
                  关于麻花影院
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  聚合最新电影高清大片资源，为影迷打造极致的在线观影体验
                </p>
              </div>
            </section>

            {/* Brand Story */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                      品牌故事
                    </h2>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      麻花影院创立于2020年，由一群热爱电影的技术极客和影视爱好者共同打造。我们的初心很简单：让每个人都能轻松、免费地观看到自己喜欢的电影。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      在过去的几年中，麻花影院从一个简单的电影聚合网站，逐步成长为涵盖动作片、科幻片、喜剧片、爱情片、恐怖片、动画片、战争片、纪录片等八大品类的综合影视平台。我们始终坚持以用户体验为核心，不断优化播放技术和内容推荐算法。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      如今，麻花影院日均服务数十万影迷用户，成为中文互联网上备受欢迎的电影在线观看平台之一。我们的使命从未改变：聚合优质影视资源，传播电影文化之美。
                    </p>
                  </div>
                  <div className="relative">
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Modern%20home%20theater%20setup%20with%20large%20projector%20screen%2C%20comfortable%20red%20theater%20seats%2C%20warm%20ambient%20lighting%2C%20cozy%20cinema%20room%2C%20movie%20posters%20on%20wall%2C%20cinematic%20atmosphere%2C%20professional%20photography%2C%20warm%20golden%20and%20red%20tones&width=1200&height=675&seq=about-cinema-01&orientation=landscape"
                        alt="麻花影院品牌形象"
                        title="麻花影院 - 品牌形象"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    { value: '50,000+', label: '影片资源' },
                    { value: '800万+', label: '月活用户' },
                    { value: '99.9%', label: '播放成功率' },
                    { value: '24/7', label: '内容更新' },
                  ].map((stat, i) => (
                    <div key={i} className="text-center bg-background-100 rounded-lg p-6">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-400">{stat.value}</div>
                      <div className="text-sm text-foreground-500 mt-2">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      name: '影迷小李',
                      comment: '麻花影院的片源真的很全，更新也快。现在每天下班回家第一件事就是打开看看有没有新片上线，已经成为我的固定习惯了。',
                      rating: 5,
                    },
                    {
                      name: '电影发烧友阿杰',
                      comment: '作为一个看了二十年电影的老影迷，麻花影院的分类和推荐真的做得很好。科幻片和动作片的资源尤其丰富，画质也没得说。',
                      rating: 5,
                    },
                    {
                      name: '文艺青年小雨',
                      comment: '除了热门大片，这里的纪录片分类也很用心。大地之歌这种优质纪录片在上面就能看到，而且完全免费，太良心了！',
                      rating: 5,
                    },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-5">
                      <div className="flex gap-0.5 mb-3">
                        {Array.from({ length: review.rating }).map((_, j) => (
                          <div key={j} className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500"></i>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.comment}</p>
                      <p className="text-xs text-foreground-400">—— {review.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-background-100 rounded-lg p-6">
                    <h3 className="font-heading font-bold text-foreground-950 mb-4">联系方式</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">电子邮箱</p>
                          <p className="text-sm text-foreground-700">contact@mahuacinema.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">客服热线</p>
                          <p className="text-sm text-foreground-700">400-888-6688</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                          <i className="ri-wechat-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">微信公众号</p>
                          <p className="text-sm text-foreground-700">麻花影院官方</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">公司地址</p>
                          <p className="text-sm text-foreground-700">北京市朝阳区望京科技园A座12层</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-6">
                    <h3 className="font-heading font-bold text-foreground-950 mb-4">合作与反馈</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      我们始终欢迎各类合作与建议。如果您是影视内容提供方、广告合作方，或是对麻花影院有任何建议和反馈，请通过以下方式联系我们。您的每一条意见我们都会认真对待。
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center text-accent-400">
                          <i className="ri-checkbox-circle-fill text-sm"></i>
                        </div>
                        <span className="text-sm text-foreground-600">内容合作：content@mahuacinema.com</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center text-accent-400">
                          <i className="ri-checkbox-circle-fill text-sm"></i>
                        </div>
                        <span className="text-sm text-foreground-600">商务合作：business@mahuacinema.com</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center text-accent-400">
                          <i className="ri-checkbox-circle-fill text-sm"></i>
                        </div>
                        <span className="text-sm text-foreground-600">用户反馈：feedback@mahuacinema.com</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-3xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {faqItems.map((item, i) => (
                    <div key={i} className="bg-background-100 rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                        onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                      >
                        <h3 className="text-sm font-bold text-foreground-950 pr-4">{item.question}</h3>
                        <div className={`w-5 h-5 flex items-center justify-center transition-transform text-foreground-400 flex-shrink-0 ${activeFaq === i ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </button>
                      <div
                        className={`transition-all duration-300 ${
                          activeFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}